import {compact, map} from 'lodash';
import React from 'react';
import MiniInfoCard from 'shared/components/college-profile/mini-info-card';
import MiniInfoCardContainer from 'shared/components/college-profile/mini-info-card-container';
import InfoTooltip from 'shared/components/info-tooltip';
import {titleize, withCommas} from 'shared/utils';
import {UserType} from 'src/types/enums';

const usNewsListTypeMap = (listType) =>
  `US News ${titleize((listType || 'national').replaceAll('_', ' '))}`;

interface Props {
  college: {
    control?: string;
    setting?: string;
    category?: string;
    profitStatus?: string;
    size?: string;
    averageNetPrice?: number;
    acceptanceRate?: number | null;
    totalUndergraduates?: number;
    tuition?: number;
    salaryAfterGraduation?: number;
    fourYearGraduationRate?: number;
    partnerSubmissionDeadlineMonth?: number;
    partnerSubmissionDeadlineDay?: number;
    forbesRank?: number;
    forbesRankYear?: number;
    usNewsRank?: number;
    usNewsRankYear?: number;
    usNewsListType?: string;
    customQuickStats?: {
      iconType: string;
      label: string;
      value: string;
      _id: string;
    }[];
  };
  userInfo: {
    user: string;
    highSchoolFrlPercentage: number;
    highSchoolInfo: {lunchPlan?: string};
    image: string;
    householdIncomeRange: string;
    [key: string]: unknown;
  };
}

const QuickStats = ({college, userInfo}: Props) => {
  const {
    control,
    setting,
    category,
    profitStatus,
    size,
    averageNetPrice,
    acceptanceRate,
    totalUndergraduates,
    tuition,
    salaryAfterGraduation,
    fourYearGraduationRate,
    partnerSubmissionDeadlineMonth,
    partnerSubmissionDeadlineDay,
    forbesRank,
    forbesRankYear,
    usNewsRank,
    usNewsRankYear,
    usNewsListType,
    customQuickStats,
  } = college;

  const getSubtitle = () => (
    <ul className="college-profile-quick-stats-list">
      {map(compact([control, setting, size, category, profitStatus]), (item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );

  const {user} = userInfo;

  if (
    !averageNetPrice &&
    !acceptanceRate &&
    !totalUndergraduates &&
    !tuition &&
    !salaryAfterGraduation &&
    !fourYearGraduationRate &&
    !forbesRank &&
    !usNewsRank &&
    !partnerSubmissionDeadlineMonth &&
    !partnerSubmissionDeadlineDay &&
    !customQuickStats?.length
  ) {
    return null;
  }

  return (
    <section className="college-profile-quick-stats">
      <header className="college-profile-quick-stats-header-container">
        <div className="college-profile-quick-stats-header">Quick Stats</div>
        <div className="college-profile-quick-stats-subtitle">{getSubtitle()}</div>
      </header>
      <div className="college-profile-quick-stats-slider">
        <MiniInfoCardContainer>
          {customQuickStats?.map(({iconType, label, value, _id}) => (
            <MiniInfoCard iconType={iconType} label={label} value={value} key={_id} />
          ))}
          <MiniInfoCard
            iconType="money-bill-alt"
            label="Average Annual Cost"
            value={averageNetPrice ? `$${withCommas(Math.round(averageNetPrice))}` : undefined}
          />
          <MiniInfoCard
            iconType="envelope"
            label="Acceptance Rate"
            value={acceptanceRate ? `${acceptanceRate}%` : undefined}
          />
          <MiniInfoCard
            iconType="users"
            label="Undergrad Students"
            value={totalUndergraduates ? withCommas(totalUndergraduates) : undefined}
          />
          <MiniInfoCard
            iconType="dollar-sign"
            label={
              <span>
                Annual Tuition
                <br />
                and Fees{' '}
                {user === UserType.PUBLIC && (
                  <InfoTooltip className="college-profile-quick-stats-tuition-info" iconSize={14}>
                    The tuition listed the out-of-state amount. Sign up or log in to see in-state
                    tuition.
                  </InfoTooltip>
                )}
              </span>
            }
            value={tuition ? `$${withCommas(tuition)}` : undefined}
          />
          <MiniInfoCard
            iconType="graduation-cap"
            label="Salary After Graduating"
            value={salaryAfterGraduation ? `$${withCommas(salaryAfterGraduation)}` : undefined}
          />
          <MiniInfoCard
            iconType="graduation-cap"
            label="Four-Year Grad Rate"
            value={fourYearGraduationRate ? `${Math.round(fourYearGraduationRate)}%` : undefined}
          />
          <MiniInfoCard
            iconType="chart-line"
            label={
              <>
                {forbesRankYear}
                {forbesRankYear && <br />}
                Forbes{forbesRankYear ? ' ' : <br />}Rank
              </>
            }
            value={forbesRank}
          />
          <MiniInfoCard
            iconType="globe"
            label={
              <>
                {usNewsRankYear}
                {usNewsRankYear && <br />}
                <div title={usNewsListTypeMap(usNewsListType)}>
                  {usNewsListTypeMap(usNewsListType)}
                </div>
              </>
            }
            value={usNewsRank}
          />
          {user === UserType.EDUCATOR && (
            <MiniInfoCard
              iconType="calendar-alt"
              label="Earning Deadline"
              value={
                partnerSubmissionDeadlineMonth && partnerSubmissionDeadlineDay
                  ? `${partnerSubmissionDeadlineMonth + 1}/${partnerSubmissionDeadlineDay}`
                  : undefined
              }
            />
          )}
        </MiniInfoCardContainer>
      </div>
    </section>
  );
};

export default QuickStats;
